import { GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';

/**
 * Helper functions for MUI X DataGrid v8 row selection model
 *
 * V8-NATIVE APPROACH:
 * - Store GridRowSelectionModel directly in state
 * - Pass it directly to DataGrid (no conversion needed)
 * - Only extract IDs when needed for operations (API calls, filtering, etc.)
 *
 * Example usage:
 * ```tsx
 * const [selection, setSelection] = useState<GridRowSelectionModel>(createSelectionModel());
 *
 * <DataGrid
 *   rowSelectionModel={selection}
 *   onRowSelectionModelChange={setSelection}
 * />
 *
 * const handleBulkAction = () => {
 *   const ids = getSelectedIds(selection);
 *   // ... use ids for API call
 * };
 * ```
 */

/**
 * Create an empty selection model for initializing state
 */
export const createSelectionModel = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
});

/**
 * Extract selected IDs as a string array from a GridRowSelectionModel
 * Use this when you need IDs for operations (API calls, filtering, etc.)
 */
export const getSelectedIds = (model: GridRowSelectionModel): string[] => {
  if (!model || !model.ids) {
    return [];
  }
  return Array.from(model.ids).map(id => String(id));
};

/**
 * Get the count of selected items
 */
export const getSelectionCount = (model: GridRowSelectionModel): number => {
  if (!model || !model.ids) {
    return 0;
  }
  return model.ids.size;
};

/**
 * Check if a specific row ID is selected
 */
export const isSelected = (model: GridRowSelectionModel, id: GridRowId): boolean => {
  if (!model || !model.ids) {
    return false;
  }
  // Handle both string and number IDs
  return model.ids.has(id) || model.ids.has(String(id)) || model.ids.has(Number(id));
};

/**
 * Check if any rows are selected
 */
export const hasSelection = (model: GridRowSelectionModel): boolean => {
  return getSelectionCount(model) > 0;
};

// ============================================================================
// LEGACY ALIASES (for backward compatibility during migration)
// These can be removed once all components are fully migrated
// ============================================================================

/** @deprecated Use createSelectionModel() instead */
export const emptySelectionModel = createSelectionModel;

/** @deprecated Use getSelectedIds() instead */
export const fromSelectionModel = getSelectedIds;

/** @deprecated No longer needed - store GridRowSelectionModel directly in state */
export const toSelectionModel = (ids: (string | number)[]): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(ids as GridRowId[]),
});
